import { FC, Fragment, useContext, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, OrdersActionKind, useOrdersAction } from '../Assistant/ordersActions';
import { useTranslation } from 'react-i18next';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import { changeUser, checkStatus, createTicket, updateTicket } from 'services/myOrders';
import { showSuccess } from 'utils/utils';
import Button from 'components/ui/Button/Button';
import { faCog, faEdit, faEnvelope, faFileInvoiceDollar, faPencilSquare, faPercent, faPlane, faStickyNote } from '@fortawesome/free-solid-svg-icons';
import MoSupportUpdateModal from './MoSupportUpdateModal';
import Dropdown from 'components/shared/Dropdown';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MoSupportTicketModal from './MoSupportTicketModal';
import MoLockPnrModal from './MoLockPnrModal';
import MoChangeUserModal from './MoChangeUserModal';
import MoChangeGrossModal from '../MoModal/MoChangeGrossModal';
import MoRoomsAndPaxesModal from '../MoModal/MoRoomsAndPaxesModal';
import MoFlightDetails from '../MoModal/MoFlightDetails';
import MoSEMain from '../MoModal/MoSEMain';
import MoSaveReportModal from '../MoModal/MoSaveReportModal';
import { MainContext } from 'contexts/MainContext';
interface MoCogProps {
  myOrder: MyOrder;
  user: any;
  onSuccess: (order: MyOrder) => void;
  dropdownProps?: any;
}

const MoCog: FC<MoCogProps> = ({ user, ...props }) => {
  const [t] = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const { user: siteUser } = useContext(MainContext);
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.fullSegment.orderSegId === props.myOrder.fullSegment.orderSegId ||
    activeOrder?.orderRow.segmentId === props.myOrder.orderRow.segmentId;
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openModalChangeUser, setOpenModalChangeUser] = useState(false);
  const [openSaveReportModal, setOpenSaveReportModal] = useState(false);
  const [openGrossModal, setOpenGrossModal] = useState(false);
  const [openRoomsAndPaxesModal, setOpenRoomsAndPaxesModal] = useState(false);
  const [openFlightDetailsModal, setOpenFlightDetailsModal] = useState(false);
  const [openSEMainModal, setOpenSEMainModal] = useState(false);

  const onClickSubmit = async (message: string, ticketId: number, commentPublic: boolean) => {
    setIsLoading(true);
    setOpenModal(false);

    await updateTicket({
      orderId: props.myOrder.orderRow.orderId,
      segmentId: props.myOrder.fullSegment.orderSegId,
      requestMessage: message,
      subject: 'Support Update',
      commentPublic,
      ticketId,
    });

    const resp = await checkStatus(props.myOrder.fullSegment);

    if (resp?.myOrder.order.hp_Ticket) {
      showSuccess(t('mo.support_update_success'));
      props.onSuccess(resp.myOrder);
    }
    setIsLoading(false);
  };

  const onClickSubmitCreate = async (message: string, subject: string) => {
    setIsLoading(true);
    setOpenModalCreate(false);

    await createTicket({
      orderId: props.myOrder.orderRow.orderId,
      segmentId: props.myOrder.fullSegment.orderSegId,
      requestMessage: message,
      subject,
    });

    const resp = await checkStatus(props.myOrder.fullSegment);

    if (resp?.myOrder.order.hp_Ticket) {
      showSuccess(t('mo.support_ticket_success'));
      props.onSuccess(resp.myOrder);
    }
    setIsLoading(false);
  };

  const onClickSubmitChangeUser = async (userCode: number) => {
    setIsLoading(true);
    setOpenModalChangeUser(false);

    const resp = await changeUser({
      orderId: props.myOrder.orderRow.orderId,
      segmentId: props.myOrder.fullSegment.orderSegId,
      targetUserCode: userCode,
    });
    if (resp?.myOrder.order) {
      showSuccess(t('mo.change_user_success'));
      props.onSuccess(resp.myOrder);
    }
    setIsLoading(false);
  };

  const onClickSubmitChangeRoomsAndPaxes = async (roomsAndPaxes: MyOrder) => {
    props.onSuccess(roomsAndPaxes);
  };

  const onClickSubmitFlightDetails = async (flightDetails: MyOrder) => {
    props.onSuccess(flightDetails);
  };

  const cogActions: OrdersActionKind[] = [
    'support-ticket',
    'save-report',
    'change-user',
    'support-update',
    'se-general',
    'se-gross',
    'se-rooms',
    'se-details',
  ];

  useOrdersAction(
    (action) =>
      cogActions.includes(action.action) &&
      actionTargetsOrder(
        action,
        {
          segmentId: props.myOrder.orderRow.segmentId,
          altSegmentId: props.myOrder.fullSegment.orderSegId,
          orderId: props.myOrder.orderRow.orderId,
          altOrderId: props.myOrder.fullSegment.orderId,
        },
        isActive,
      ),
    (action) => {
      if (siteUser?.branchId !== 5000) return;
      if (action.action === 'support-ticket') {
        if (action.message && action.subject) void onClickSubmitCreate(action.message, action.subject);
        else setOpenModalCreate(true);
      } else if (action.action === 'save-report') {
        setOpenSaveReportModal(true);
      } else if (action.action === 'change-user') {
        if (action.userCode) void onClickSubmitChangeUser(action.userCode);
        else setOpenModalChangeUser(true);
      } else if (action.action === 'support-update') {
        if (action.message && action.ticketId) {
          void onClickSubmit(action.message, action.ticketId, !!action.commentPublic);
        } else setOpenModal(true);
      } else if (action.action === 'se-general') {
        setOpenSEMainModal(true);
      } else if (action.action === 'se-gross') {
        setOpenGrossModal(true);
      } else if (action.action === 'se-rooms') {
        setOpenRoomsAndPaxesModal(true);
      } else if (action.action === 'se-details' && props.myOrder.fullSegment.details.length > 0) {
        setOpenFlightDetailsModal(true);
      }
    },
    isActive,
  );

  useOrdersAction(
    (action) =>
      action.action === 'lock-pnr' &&
      siteUser?.branchId !== 5000 &&
      actionTargetsOrder(
        action,
        {
          segmentId: props.myOrder.orderRow.segmentId,
          altSegmentId: props.myOrder.fullSegment.orderSegId,
          orderId: props.myOrder.orderRow.orderId,
          altOrderId: props.myOrder.fullSegment.orderId,
        },
        isActive,
      ),
    () => undefined,
    isActive,
  );

  return (
    <Fragment>
      {siteUser?.branchId === 5000 && (
        <Dropdown
          bottom={30}
          right={-115}
          left={0}
          openerEmpty={true}
          byClick={true}
          {...props.dropdownProps}
          opener={
            <Button
              icon={faCog}
              title=""
              className="btn btn-primary btn-ssm"
              disabled={isLoading}
            />
          }
        >
          <div className="c-dropdown-row" onClick={() => setOpenModalCreate(true)}>
            <div>
              <FontAwesomeIcon icon={faEnvelope} className="mr-5" size="sm" />
              {t('mo.support_ticket')}
            </div>
          </div>
          <div className="c-dropdown-row" onClick={() => setOpenSaveReportModal(true)}>
            <div>
              <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-5" size="sm" />
              Save report
            </div>
          </div>
          <div className="c-dropdown-row" onClick={() => setOpenModalChangeUser(true)}>
            <div>
              <FontAwesomeIcon icon={faStickyNote} className="mr-5" size="sm" />
              {t('mo.change_user')}
            </div>
          </div>
          <div className="c-dropdown-row nounderline" onClick={() => setOpenModal(true)}>
            <div>
              <FontAwesomeIcon icon={faStickyNote} className="mr-5" size="sm" />
              {t('mo.support_update')}
            </div>
          </div>
          {siteUser?.branchId === 5000 && (
            <Fragment>
              <div className="c-dropdown-row nounderline" onClick={() => setOpenSEMainModal(true)}>
                <FontAwesomeIcon icon={faEdit} className="mr-5" size="sm" />
                <span>S.E. General</span>
              </div>
              <div className="c-dropdown-row nounderline" onClick={() => setOpenGrossModal(true)}>
                <FontAwesomeIcon icon={faPercent} className="mr-5" size="sm" />
                <span>S.E. Gross</span>
              </div>
              <div className={"c-dropdown-row" + (props.myOrder.fullSegment.details.length > 0 ? ' nounderline' : '')} onClick={() => setOpenRoomsAndPaxesModal(true)}>
                <FontAwesomeIcon icon={faPencilSquare} className="mr-5" size="sm" />
                <span>S.E. Rooms & Paxes</span>
              </div>
              {props.myOrder.fullSegment.details.length > 0 && (
                <div className="c-dropdown-row" onClick={() => setOpenFlightDetailsModal(true)}>
                  <FontAwesomeIcon icon={faPlane} className="mr-5" size="sm" />
                  <span>S.E. Details</span>
                </div>
              )}
            </Fragment>
          )}
          <MoLockPnrModal orderRow={props.myOrder.orderRow} fullSegment={props.myOrder.fullSegment} onSuccess={props.onSuccess} />
        </Dropdown>
      )}
      {siteUser?.branchId === 5000 && (
        <MoLockPnrModal
          hideMenu
          orderRow={props.myOrder.orderRow}
          fullSegment={props.myOrder.fullSegment}
          onSuccess={props.onSuccess}
        />
      )}

      {openModal && (
        <MoSupportUpdateModal
          segment={props.myOrder.fullSegment}
          user={user}
          openModal={openModal}
          setOpenModal={setOpenModal}
          onSubmit={onClickSubmit}
        />
      )}

      {openModalCreate && (
        <MoSupportTicketModal
          segment={props.myOrder.fullSegment}
          user={user}
          openModal={openModalCreate}
          setOpenModal={setOpenModalCreate}
          onSubmit={onClickSubmitCreate}
        />
      )}
      {openModalChangeUser && (siteUser?.branchId || 0) > 40 && (
        <MoChangeUserModal
          user={user}
          openModal={openModalChangeUser}
          setOpenModal={setOpenModalChangeUser}
          onSubmit={onClickSubmitChangeUser}
          segment={props.myOrder.fullSegment}
        />
      )}
      {openSaveReportModal && (
        <MoSaveReportModal
          openModal={openSaveReportModal}
          setOpenModal={setOpenSaveReportModal}
          onSubmit={props.onSuccess}
          myOrder={props.myOrder}
        />
      )}
      {openGrossModal && (
        <MoChangeGrossModal
          openModal={openGrossModal}
          setOpenModal={setOpenGrossModal}
          onSubmit={props.onSuccess}
          myOrder={props.myOrder}
        />
      )}
      {openRoomsAndPaxesModal && (
        <MoRoomsAndPaxesModal
          openModal={openRoomsAndPaxesModal}
          setOpenModal={setOpenRoomsAndPaxesModal}
          onSubmit={onClickSubmitChangeRoomsAndPaxes}
          myOrder={props.myOrder}
        />
      )}
      {openFlightDetailsModal && (
        <MoFlightDetails
          openModal={openFlightDetailsModal}
          setOpenModal={setOpenFlightDetailsModal}
          onSubmit={onClickSubmitFlightDetails}
          myOrder={props.myOrder}
        />
      )}
      {openSEMainModal && (
        <MoSEMain
          openModal={openSEMainModal}
          setOpenModal={setOpenSEMainModal}
          onSubmit={props.onSuccess}
          myOrder={props.myOrder}
        />
      )}
    </Fragment>
  );
};

export default withErrorBoundary(MoCog, 'MoCog');
